import React, { useState } from "react";
import SubPageLayout from "../../../components/shared/SubPageLayout";

import logger from '../../../utils/logger';

const Branding = () => {
  const [selectedCategory, setSelectedCategory] = useState("logos");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);

  const breadcrumbItems = [
    { label: "Administrative", link: "/administrative" },
    { label: "Branding", link: "/administrative/branding" },
  ];

  // Mock data for assets
  const mockAssets = [
    {
      id: 1,
      name: "Company Logo Primary",
      type: "logos",
      thumbnail: "/mock/logo-primary.png",
      uploadDate: "2024-03-20",
      status: "approved",
      version: 2,
    },
    {
      id: 2,
      name: "Business Card Template",
      type: "business-cards",
      thumbnail: "/mock/business-card.png",
      uploadDate: "2024-03-19",
      status: "pending",
      version: 1,
    },
    // Add more mock assets as needed
  ];

  const assetCategories = [
    { id: "logos", label: "Logos" },
    { id: "business-cards", label: "Business Cards" },
    { id: "email-signatures", label: "Email Signatures" },
    { id: "templates", label: "Templates" },
    { id: "icons", label: "Icons" },
  ];

  const handleUploadAsset = () => {
    // Implement asset upload logic
    logger.info("Upload asset clicked");
  };

  const handleCreateTemplate = () => {
    setShowTemplateEditor(true);
  };

  const handleDownloadBrandingKit = () => {
    // Implement branding kit download logic
    logger.info("Download branding kit clicked");
  };

  const actionButton = (
    <div className="flex space-x-4">
      <button
        onClick={handleUploadAsset}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#029831] hover:bg-[#018529] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#029831]"
      >
        <svg
          className="-ml-1 mr-2 h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Upload New Asset
      </button>
      <button
        onClick={handleCreateTemplate}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202EC6] hover:bg-[#1925A3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6]"
      >
        <svg
          className="-ml-1 mr-2 h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
        </svg>
        Create New Template
      </button>
      <button
        onClick={handleDownloadBrandingKit}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6]"
      >
        <svg
          className="-ml-1 mr-2 h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        Download Branding Kit
      </button>
    </div>
  );

  return (
    <SubPageLayout
      title="Branding Management"
      subtitle="Manage and maintain your firm's brand identity across all channels"
      breadcrumbItems={breadcrumbItems}
      actionButton={actionButton}
    >
      {/* Search & Filter Bar */}
      <div className="mb-8 flex space-x-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search assets..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-surface-default dark:bg-surface-default placeholder-gray-500 focus:outline-none focus:ring-[#202EC6] focus:border-[#202EC6] sm:text-sm"
            />
          </div>
        </div>
        <div className="flex space-x-4">
          <select
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
            className="block w-40 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#202EC6] focus:border-[#202EC6] sm:text-sm rounded-md"
          >
            <option value="all">All Assets</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="block w-40 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#202EC6] focus:border-[#202EC6] sm:text-sm rounded-md"
          >
            <option value="date">Date Added</option>
            <option value="name">Name</option>
            <option value="type">Type</option>
          </select>
        </div>
      </div>

      {/* Asset Categories */}
      <div className="mb-8">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {assetCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    selectedCategory === category.id
                      ? "border-[#202EC6] text-[#202EC6]"
                      : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
                  }
                `}
              >
                {category.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Asset Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {mockAssets
          .filter(
            (asset) =>
              (selectedCategory === "all" || asset.type === selectedCategory) &&
              (filterBy === "all" || asset.status === filterBy) &&
              asset.name.toLowerCase().includes(searchQuery.toLowerCase()),
          )
          .sort((a, b) => {
            if (sortBy === "date")
              return new Date(b.uploadDate) - new Date(a.uploadDate);
            if (sortBy === "name") return a.name.localeCompare(b.name);
            return 0;
          })
          .map((asset) => (
            <div
              key={asset.id}
              className="relative group bg-surface-default dark:bg-surface-default border rounded-lg shadow-sm overflow-hidden hover:shadow-md dark:shadow-none transition-shadow duration-200"
            >
              <div className="aspect-w-4 aspect-h-3">
                <img
                  src={asset.thumbnail || "/placeholder-image.png"}
                  alt={asset.name}
                  className="object-cover"
                />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {asset.name}
                </h3>
                <div className="mt-1 flex justify-between items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {new Date(asset.uploadDate).toLocaleDateString()}
                  </span>
                  <span
                    className={`
                    inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${
                      asset.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : asset.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                    }
                  `}
                  >
                    {asset.status}
                  </span>
                </div>
                <div className="mt-4 flex justify-between">
                  <button className="text-sm text-[#202EC6] hover:text-[#1925A3]">
                    View Details
                  </button>
                  <div className="flex space-x-2">
                    <button className="text-gray-400 hover:text-gray-500 dark:text-gray-500">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                    </button>
                    <button className="text-gray-400 hover:text-gray-500 dark:text-gray-500">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button className="text-gray-400 hover:text-red-500">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-default dark:bg-surface-default rounded-lg w-full max-w-6xl h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Template Editor
              </h2>
              <button
                onClick={() => setShowTemplateEditor(false)}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 flex">
              {/* Toolbar */}
              <div className="w-64 border-r border-gray-200 dark:border-gray-700 p-4">
                <div className="space-y-4">
                  <button className="w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
                    Add Text
                  </button>
                  <button className="w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
                    Add Image
                  </button>
                  <button className="w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
                    Add Shape
                  </button>
                </div>
              </div>
              {/* Canvas Area */}
              <div className="flex-1 bg-gray-50 p-8">
                <div className="bg-surface-default dark:bg-surface-default w-full h-full rounded-lg shadow-inner border border-gray-200 dark:border-gray-700">
                  {/* Canvas content goes here */}
                </div>
              </div>
              {/* Properties Panel */}
              <div className="w-64 border-l border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Properties
                </h3>
                {/* Properties content goes here */}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-4">
              <button
                onClick={() => setShowTemplateEditor(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:text-gray-500"
              >
                Cancel
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-[#202EC6] hover:bg-[#1925A3] rounded-md">
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}
    </SubPageLayout>
  );
};

export default Branding;
